import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Play, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { StagePanel } from "./StagePanel";
import { ConfigSummary } from "./ConfigSummary";
import * as pipelineValidator from "@/services/pipelineValidator";
import type { PVEmployeeAuditResult } from "@/types";

const STAGES = [
	"config",
	"attendance",
	"leave",
	"timesheet",
	"payroll",
	"payslip",
	"weekend",
] as const;

type Stage = (typeof STAGES)[number];

const STAGE_LABELS: Record<Stage, string> = {
	config: "Config",
	attendance: "Attendance",
	leave: "Leave",
	timesheet: "Timesheet",
	payroll: "Payroll",
	payslip: "Payslip",
	weekend: "Weekend",
};

const STATUS_COLORS: Record<string, string> = {
	pass: "bg-green-500",
	warn: "bg-yellow-500",
	fail: "bg-red-500",
};

interface PipelineRunnerProps {
	organizationId: string;
	periodStart: string;
	periodEnd: string;
}

function StatusDot({ status }: { status: string }) {
	return (
		<span
			className={`inline-block h-2.5 w-2.5 rounded-full ${STATUS_COLORS[status] ?? "bg-gray-300"}`}
		/>
	);
}

function EmployeeInfoBar({ result }: { result: PVEmployeeAuditResult }) {
	return (
		<div className="flex flex-wrap items-center gap-3 text-sm">
			<span className="font-medium">{result.employeeName}</span>
			<Badge variant="outline">{result.employmentType}</Badge>
			<Badge variant="outline">{result.salaryBasis}</Badge>
			<Badge variant="outline">{result.payFrequency}</Badge>
			<span className="text-muted-foreground ml-auto">
				{result.passedChecks}/{result.totalChecks} checks passed
			</span>
		</div>
	);
}

function Stepper({
	result,
	activeStage,
	onStageClick,
}: {
	result: PVEmployeeAuditResult;
	activeStage: Stage;
	onStageClick: (stage: Stage) => void;
}) {
	return (
		<div className="flex items-center gap-1 overflow-x-auto pb-1">
			{STAGES.map((stage, idx) => {
				const stageResult = result.stages[stage];
				const isActive = stage === activeStage;

				return (
					<div key={stage} className="flex items-center">
						{idx > 0 && (
							<div className="h-px w-4 bg-border shrink-0" />
						)}
						<button
							type="button"
							onClick={() => onStageClick(stage)}
							className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors whitespace-nowrap ${
								isActive
									? "bg-accent font-medium"
									: "hover:bg-accent/50"
							}`}
						>
							<StatusDot status={stageResult.status} />
							{STAGE_LABELS[stage]}
						</button>
					</div>
				);
			})}
		</div>
	);
}

export function PipelineRunner({
	organizationId,
	periodStart,
	periodEnd,
}: PipelineRunnerProps) {
	const [userId, setUserId] = useState("");
	const [activeStage, setActiveStage] = useState<Stage>("config");

	const auditMutation = useMutation({
		mutationFn: (uid: string) =>
			pipelineValidator.runEmployeeAudit(uid, {
				organizationId,
				periodStart,
				periodEnd,
			}),
		onSuccess: () => {
			setActiveStage("config");
		},
	});

	const result = auditMutation.data;
	const activeIndex = STAGES.indexOf(activeStage);

	function handleRunAudit() {
		const trimmed = userId.trim();
		if (!trimmed || !organizationId) return;
		auditMutation.mutate(trimmed);
	}

	function handlePrev() {
		if (activeIndex > 0) {
			setActiveStage(STAGES[activeIndex - 1]);
		}
	}

	function handleNext() {
		if (activeIndex < STAGES.length - 1) {
			setActiveStage(STAGES[activeIndex + 1]);
		}
	}

	return (
		<div className="space-y-4">
			{/* Employee selector */}
			<Card>
				<CardContent className="pt-4">
					<div className="flex items-end gap-3">
						<div className="flex-1 space-y-2">
							<Label htmlFor="runner-user-id">User ID</Label>
							<Input
								id="runner-user-id"
								placeholder="Enter employee user ID"
								value={userId}
								onChange={(e) => setUserId(e.target.value)}
								onKeyDown={(e) => {
									if (e.key === "Enter") handleRunAudit();
								}}
							/>
						</div>
						<Button
							onClick={handleRunAudit}
							disabled={
								!userId.trim() ||
								!organizationId ||
								auditMutation.isPending
							}
						>
							{auditMutation.isPending ? (
								<Loader2 className="h-4 w-4 animate-spin mr-2" />
							) : (
								<Play className="h-4 w-4 mr-2" />
							)}
							Run Audit
						</Button>
					</div>
				</CardContent>
			</Card>

			{/* Error state */}
			{auditMutation.isError && (
				<Card>
					<CardContent className="py-4">
						<p className="text-sm text-red-500">
							{auditMutation.error instanceof Error
								? auditMutation.error.message
								: "Audit failed"}
						</p>
					</CardContent>
				</Card>
			)}

			{/* Results */}
			{result && (
				<>
					{/* Employee info bar */}
					<Card>
						<CardContent className="py-3">
							<EmployeeInfoBar result={result} />
						</CardContent>
					</Card>

					{/* Config summary */}
					<ConfigSummary configSummary={result.configSummary} />

					{/* Stepper */}
					<Card>
						<CardContent className="py-3">
							<Stepper
								result={result}
								activeStage={activeStage}
								onStageClick={setActiveStage}
							/>
						</CardContent>
					</Card>

					{/* Stage content */}
					<Card>
						<CardContent className="pt-4">
							<h3 className="font-medium mb-3">
								{STAGE_LABELS[activeStage]}
							</h3>
							<StagePanel
								checks={result.stages[activeStage].checks}
							/>
						</CardContent>
					</Card>

					{/* Navigation */}
					<div className="flex items-center justify-between">
						<Button
							variant="outline"
							onClick={handlePrev}
							disabled={activeIndex === 0}
						>
							<ChevronLeft className="h-4 w-4 mr-1" />
							Back
						</Button>
						<span className="text-sm text-muted-foreground">
							{activeIndex + 1} / {STAGES.length}
						</span>
						<Button
							variant="outline"
							onClick={handleNext}
							disabled={activeIndex === STAGES.length - 1}
						>
							Next
							<ChevronRight className="h-4 w-4 ml-1" />
						</Button>
					</div>
				</>
			)}
		</div>
	);
}
